import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  ArchiveDestinationMultiplierPayload,
  CreateDestinationMultiplierPayload,
  DestinationMultiplierGroup,
  DestinationMultiplierState,
  UpdateDestinationMultiplierExpiryPayload
} from './types';

export const DESTINATION_MULTIPLIERS_URL = '/api/product/destination-multipliers';
export const DESTINATION_MULTIPLIERS_ACTIVE_URL = '/api/product/destination-multipliers/active';
export const DESTINATION_MULTIPLIERS_ARCHIVE_URL = '/api/product/destination-multipliers/archive';
export const DESTINATION_MULTIPLIERS_EXPIRY_URL = '/api/product/destination-multipliers/expiry';

const PRODUCT_API_V2_CONFIG = { headers: { 'api-version': '2' } };

export const initialState: DestinationMultiplierState = {
  activeGroups: [],
  isLoading: false,
  error: null,
  isSaving: false,
  saveError: null,
  isArchiving: false,
  archiveError: null,
  isEditing: false,
  editError: null
};

const DESTINATION_MULTIPLIER_ERROR_LABEL_KEYS: Record<string, string> = {
  MULTIPLIER_DATE_RANGE_OVERLAP: 'destination_multipliers.errors.multiplierDateRangeOverlap',
  MULTIPLIER_ACTIVE_WITHOUT_EXPIRY: 'destination_multipliers.errors.multiplierActiveWithoutExpiry'
};

interface DestinationMultiplierApiErrorData {
  error?: string;
  errors?: { message?: string }[];
  message?: string;
  code?: string;
  errorCode?: string;
}

interface DestinationMultiplierApiError {
  response?: { data?: DestinationMultiplierApiErrorData };
}

const isDestinationMultiplierApiError = (err: unknown): err is DestinationMultiplierApiError =>
  typeof err === 'object' && err !== null && 'response' in err;

const extractApiMessage = (err: unknown, fallbackLabelKey: string): string => {
  const data: DestinationMultiplierApiErrorData | undefined = isDestinationMultiplierApiError(err)
    ? err.response?.data
    : undefined;
  // Only trust messages the API actually put in the response body. Axios transport
  // failures produce generic strings like "Request failed with status code 500" or
  // "Network Error", which are not user-facing, so those fall back to the TMS label.
  const rawMessage = data?.error || (Array.isArray(data?.errors) && data.errors[0]?.message) || data?.message;
  const code = data?.code ?? data?.errorCode ?? rawMessage;
  if (code && DESTINATION_MULTIPLIER_ERROR_LABEL_KEYS[code]) {
    return DESTINATION_MULTIPLIER_ERROR_LABEL_KEYS[code];
  }
  return rawMessage || fallbackLabelKey;
};

export const fetchActiveDestinationMultipliers = createAsyncThunk<
  DestinationMultiplierGroup[],
  void,
  { rejectValue: string }
>('destinationMultiplier/fetchActive', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get<DestinationMultiplierGroup[]>(
      DESTINATION_MULTIPLIERS_ACTIVE_URL,
      PRODUCT_API_V2_CONFIG
    );
    return response.data ?? [];
  } catch (err: unknown) {
    return rejectWithValue(extractApiMessage(err, 'destination_multipliers.errors.load'));
  }
});

export const createDestinationMultiplier = createAsyncThunk<
  void,
  CreateDestinationMultiplierPayload,
  { rejectValue: string }
>('destinationMultiplier/create', async (payload, { dispatch, rejectWithValue }) => {
  try {
    await axios.post(DESTINATION_MULTIPLIERS_URL, payload, PRODUCT_API_V2_CONFIG);
    await dispatch(fetchActiveDestinationMultipliers());
    return undefined;
  } catch (err: unknown) {
    return rejectWithValue(extractApiMessage(err, 'destination_multipliers.errors.create'));
  }
});

export const archiveDestinationMultiplier = createAsyncThunk<
  void,
  ArchiveDestinationMultiplierPayload,
  { rejectValue: string }
>('destinationMultiplier/archive', async (payload, { dispatch, rejectWithValue }) => {
  try {
    await axios.post(DESTINATION_MULTIPLIERS_ARCHIVE_URL, payload, PRODUCT_API_V2_CONFIG);
    await dispatch(fetchActiveDestinationMultipliers());
    return undefined;
  } catch (err: unknown) {
    return rejectWithValue(extractApiMessage(err, 'destination_multipliers.errors.archive'));
  }
});

export const updateDestinationMultiplierExpiry = createAsyncThunk<
  void,
  UpdateDestinationMultiplierExpiryPayload,
  { rejectValue: string }
>('destinationMultiplier/updateExpiry', async (payload, { dispatch, rejectWithValue }) => {
  try {
    await axios.patch(DESTINATION_MULTIPLIERS_EXPIRY_URL, payload, PRODUCT_API_V2_CONFIG);
    await dispatch(fetchActiveDestinationMultipliers());
    return undefined;
  } catch (err: unknown) {
    return rejectWithValue(extractApiMessage(err, 'destination_multipliers.errors.updateExpiry'));
  }
});

const destinationMultiplierSlice = createSlice({
  name: 'destinationMultiplier',
  initialState,
  reducers: {
    resetDestinationMultiplierError(state) {
      state.error = null;
    },
    resetDestinationMultiplierSaveError(state) {
      state.saveError = null;
    },
    resetDestinationMultiplierArchiveError(state) {
      state.archiveError = null;
    },
    resetDestinationMultiplierEditError(state) {
      state.editError = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchActiveDestinationMultipliers.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchActiveDestinationMultipliers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.activeGroups = action.payload;
      })
      .addCase(fetchActiveDestinationMultipliers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'destination_multipliers.errors.load';
      })
      .addCase(createDestinationMultiplier.pending, state => {
        state.isSaving = true;
        state.saveError = null;
      })
      .addCase(createDestinationMultiplier.fulfilled, state => {
        state.isSaving = false;
      })
      .addCase(createDestinationMultiplier.rejected, (state, action) => {
        state.isSaving = false;
        state.saveError = action.payload ?? 'destination_multipliers.errors.create';
      })
      .addCase(archiveDestinationMultiplier.pending, state => {
        state.isArchiving = true;
        state.archiveError = null;
      })
      .addCase(archiveDestinationMultiplier.fulfilled, state => {
        state.isArchiving = false;
      })
      .addCase(archiveDestinationMultiplier.rejected, (state, action) => {
        state.isArchiving = false;
        state.archiveError = action.payload ?? 'destination_multipliers.errors.archive';
      })
      .addCase(updateDestinationMultiplierExpiry.pending, state => {
        state.isEditing = true;
        state.editError = null;
      })
      .addCase(updateDestinationMultiplierExpiry.fulfilled, state => {
        state.isEditing = false;
      })
      .addCase(updateDestinationMultiplierExpiry.rejected, (state, action) => {
        state.isEditing = false;
        state.editError = action.payload ?? 'destination_multipliers.errors.updateExpiry';
      });
  }
});

export const {
  resetDestinationMultiplierError,
  resetDestinationMultiplierSaveError,
  resetDestinationMultiplierArchiveError,
  resetDestinationMultiplierEditError
} = destinationMultiplierSlice.actions;
export default destinationMultiplierSlice.reducer;
